<template>
  <div
    class="calendar-event-summary"
    :class="{
      'calendar-event-summary--muted': muted,
      'calendar-event-summary--removed': removed
    }"
  >
    <a
      class="calendar-event-summary__description"
      :href="calendarUrl"
      target="_blank"
      rel="noopener"
    >
      {{ event.description }}
    </a>
    <span class="calendar-event-summary__dates">
      {{ dateRange }}
    </span>
    <AvatarGroup
      :avatars="event.users"
      :numberOfVisibleUsers="3"
      :showHiddenAvatarsTooltip="true"
      class="calendar-event-summary__avatars"
    />
  </div>
</template>

<script setup lang="ts">
/** Renders the compact calendar-event snapshot shared by create, update, and delete notifications. */
import { useRoute, useRouter } from "vue-router"
import { computed } from "vue"

import AvatarGroup from "@/components/Avatar/AvatarGroup.vue"

import { formatDate, fromAllDayISOString } from "@/utils/dateUtil"

import type { CalendarNotificationEvent } from "@/models/Notification"

const props = withDefaults(
  defineProps<{
    event: CalendarNotificationEvent
    muted?: boolean
    removed?: boolean
  }>(),
  {
    muted: false,
    removed: false
  }
)

const route = useRoute()
const router = useRouter()

const calendarUrl = computed(
  () =>
    router.resolve({
      name: "team-calendar",
      params: { accountId: route.params.accountId }
    }).href
)

/** All-day events arrive pinned to UTC midnight, so the day is read from the UTC parts. */
const dateRange = computed(() => {
  const startDate = fromAllDayISOString(props.event.startDate)
  const endDate = fromAllDayISOString(props.event.endDate)
  const formattedStartDate = formatDate(startDate, "d MMM", { dateOnly: true })

  if (startDate.getTime() === endDate.getTime()) return formattedStartDate

  return `${formattedStartDate} - ${formatDate(endDate, "d MMM", { dateOnly: true })}`
})
</script>
